import { useMemo, useState } from "react"

import { App, Checkbox } from "antd"
import { useDispatch, useSelector } from "react-redux"

import Icon from "../../../Icon"
import { extendedAccessRoles } from "../../../Utils/permission"
import AlertsActions from "../reducer"

const useBulkAction = ({
  currentPageData,
  businessAccount,
  businessAccountId,
  updateAlerts,
  baRole,
  isAdmin
}) => {
  const dispatch = useDispatch()
  const { notification, modal } = App.useApp()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const isBulkActionEnabled = businessAccount?.bulk_action_enabled
  const isBulkActionDisabled = !extendedAccessRoles.includes(baRole) || isAdmin
  const [isBulkActionActive, setIsBulkActionActive] = useState(false)
  const [allSelected, setAllSelected] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [excludedRowKeys, setExcludedRowKeys] = useState([])
  const bulkStatusUpdateDisabled = selectedRowKeys.length === 0 && !allSelected

  const rowSelection = useMemo(() => {
    if (!isBulkActionActive) return null
    return {
      selectedRowKeys: allSelected
        ? [
            ...currentPageData
              .filter((record) => record.enabled_for_bulk)
              .map((r) => r.alert_id)
              .filter((id) => !excludedRowKeys.includes(id))
          ]
        : [...selectedRowKeys],

      onSelect: (record, selected) => {
        if (allSelected) {
          if (!selected) {
            setExcludedRowKeys((prev) => [...prev, record.alert_id])
          } else {
            setExcludedRowKeys((prev) => prev.filter((id) => id !== record.alert_id))
          }
        } else {
          setSelectedRowKeys((prev) => {
            return selected
              ? [...prev, record.alert_id]
              : prev.filter((id) => id !== record.alert_id)
          })
        }
      },

      columnTitle: (
        <Checkbox
          checked={allSelected}
          indeterminate={!allSelected && selectedRowKeys.length > 0}
          onChange={(e) => {
            if (e.target.checked) {
              setAllSelected(true)
              setSelectedRowKeys([])
              setExcludedRowKeys([])
            } else {
              setAllSelected(false)
              setSelectedRowKeys([])
              setExcludedRowKeys([])
            }
          }}
        />
      ),

      getCheckboxProps: (record) => ({
        disabled: !record?.enabled_for_bulk
      })
    }
  }, [allSelected, selectedRowKeys, excludedRowKeys, currentPageData, isBulkActionActive])

  const onBulkAction = (action) => {
    setIsBulkActionActive(true)
  }

  const cancelBulkAction = () => {
    setIsBulkActionActive(false)
    setAllSelected(false)
    setSelectedRowKeys([])
    setExcludedRowKeys([])
  }

  const onBulkStatusUpdate = () => {
    modal.confirm({
      title: "Change alerts status to “Auto-refunded”?",
      content:
        "Are you sure you want to replace the status for all selected “Attention” alerts in the table to “Auto-refunded”? This action cannot be undone.",
      okText: "Change status",
      cancelText: "Cancel",
      icon: null,
      centered: !isMobile,
      styles: { content: { backgroundImage: "none" } },
      closeIcon: <Icon type="cross_small" />,
      closable: true,
      onOk: () => {
        return new Promise((resolve) => {
          handleBulkAction({ onSuccess: resolve, onError: resolve })
        })
      }
    })
  }

  const handleBulkAction = ({ onSuccess, onError }) => {
    const data = {
      business_account_id: businessAccountId,
      mode: allSelected ? "exclude" : "include",
      alert_ids: allSelected ? excludedRowKeys : selectedRowKeys
    }

    dispatch(
      AlertsActions.alertsBulkResolveRequest({
        ...data,
        onSuccess: (data) => {
          const message =
            typeof data?.message === "string" ? data.message : "Alert(s) sent for processing."
          if (data?.accepted === 0) {
            notification.info({ message })
          } else {
            notification.success({ message })
          }
          onSuccess && onSuccess(data)
          updateAlerts && updateAlerts()
          cancelBulkAction()
        },
        onError: (data) => {
          notification.error({
            message: "Failed to send alerts for processing."
          })
          onError && onError(data)
        }
      })
    )
  }

  return {
    isBulkActionEnabled,
    isBulkActionActive,
    rowSelection,
    bulkStatusUpdateDisabled,
    isBulkActionDisabled,
    onBulkAction,
    onBulkStatusUpdate,
    cancelBulkAction
  }
}

export default useBulkAction
